import { addDays, endOfWeek, format, parseISO } from "date-fns";
import { DISPLAY_TIMEZONE, formatTime } from "@/lib/format";
import i18n from "@/lib/i18next";

export const EVENT_TIMEZONE = DISPLAY_TIMEZONE;

export type SchedulableEvent = {
  startsAtIso: string | null;
};

export function zurichDateKey(date: Date = new Date()): string {
  return date.toLocaleDateString("en-CA", { timeZone: EVENT_TIMEZONE });
}

/** Calendar date (YYYY-MM-DD) of an instant in Europe/Zurich — not the UTC ISO prefix. */
export function extractEventDateKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const trimmed = iso.trim();
  const d = new Date(trimmed);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString("en-CA", { timeZone: EVENT_TIMEZONE });
  }
  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? null;
}

export function addDaysToKey(key: string, days: number): string {
  return format(addDays(parseISO(key), days), "yyyy-MM-dd");
}

export function endOfWeekKey(key: string): string {
  return format(endOfWeek(parseISO(key), { weekStartsOn: 1 }), "yyyy-MM-dd");
}

/** Localized schedule label: Today · 22:00 / Tomorrow · 22:00 / Sat 7 June · 22:00 */
export function formatEventSchedule(startsAtIso: string | null | undefined): string {
  if (!startsAtIso) return "—";

  const time = formatTime(startsAtIso);
  const eventKey = extractEventDateKey(startsAtIso);
  if (!eventKey || time === "—") return time;

  const todayKey = zurichDateKey();
  const tomorrowKey = addDaysToKey(todayKey, 1);
  const locale = i18n.language || "de";
  const day = Number(eventKey.split("-")[2]);

  if (eventKey === todayKey) {
    return `${i18n.t("time.today", { ns: "common" })} · ${time}`;
  }
  if (eventKey === tomorrowKey) {
    return `${i18n.t("time.tomorrow", { ns: "common" })} · ${time}`;
  }

  const date = parseISO(eventKey);
  const weekday = date.toLocaleDateString(locale, { weekday: "short", timeZone: EVENT_TIMEZONE });
  const monthName = date.toLocaleDateString(locale, { month: "long", timeZone: EVENT_TIMEZONE });
  return `${weekday} ${day}. ${monthName} · ${time}`;
}

export type MonthEventSection<T extends SchedulableEvent = SchedulableEvent> = {
  monthKey: string;
  label: string;
  events: T[];
};

export function zurichMonthKey(date: Date = new Date()): string {
  return date.toLocaleDateString("en-CA", { timeZone: EVENT_TIMEZONE }).slice(0, 7);
}

export function formatMonthSectionLabel(monthKey: string): string {
  const [year, month] = monthKey.split("-");
  const date = parseISO(`${year}-${month}-01`);
  const monthName = date.toLocaleDateString("de-CH", {
    month: "long",
    year: "numeric",
    timeZone: EVENT_TIMEZONE,
  });
  return monthName.charAt(0).toUpperCase() + monthName.slice(1);
}

export function groupEventsByMonth<T extends SchedulableEvent>(
  events: T[],
): MonthEventSection<T>[] {
  const buckets = new Map<string, T[]>();

  for (const event of events) {
    const dateKey = extractEventDateKey(event.startsAtIso);
    if (!dateKey) continue;
    const monthKey = dateKey.slice(0, 7);
    const list = buckets.get(monthKey) ?? [];
    list.push(event);
    buckets.set(monthKey, list);
  }

  const sortByStart = (a: T, b: T) =>
    (a.startsAtIso ?? "").localeCompare(b.startsAtIso ?? "");

  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, monthEvents]) => ({
      monthKey,
      label: formatMonthSectionLabel(monthKey),
      events: [...monthEvents].sort(sortByStart),
    }));
}

export function formatDaySectionLabel(dateKey: string, todayKey: string = zurichDateKey()): string {
  const tomorrowKey = addDaysToKey(todayKey, 1);
  const dayNum = Number(dateKey.split("-")[2]);
  const date = parseISO(dateKey);
  const weekdayShort = date.toLocaleDateString("de-CH", {
    weekday: "short",
    timeZone: EVENT_TIMEZONE,
  });
  const weekdayLong = date.toLocaleDateString("de-CH", {
    weekday: "long",
    timeZone: EVENT_TIMEZONE,
  });
  const monthName = date.toLocaleDateString("de-CH", { month: "long", timeZone: EVENT_TIMEZONE });

  if (dateKey === todayKey) {
    return `Heute, ${weekdayShort} ${dayNum}. ${monthName}`;
  }
  if (dateKey === tomorrowKey) {
    return `Morgen, ${weekdayShort} ${dayNum}. ${monthName}`;
  }
  const capitalizedWeekday = weekdayLong.charAt(0).toUpperCase() + weekdayLong.slice(1);
  return `${capitalizedWeekday} ${dayNum}. ${monthName}`;
}

export type DayEventSection<T extends SchedulableEvent = SchedulableEvent> = {
  dateKey: string;
  label: string;
  events: T[];
};

export function groupEventsByDay<T extends SchedulableEvent>(
  events: T[],
  maxDays = 7,
  todayKey: string = zurichDateKey(),
): DayEventSection<T>[] {
  const endKey = addDaysToKey(todayKey, maxDays - 1);
  const buckets = new Map<string, T[]>();

  for (const event of events) {
    const dateKey = extractEventDateKey(event.startsAtIso);
    if (!dateKey || dateKey < todayKey || dateKey > endKey) continue;
    const list = buckets.get(dateKey) ?? [];
    list.push(event);
    buckets.set(dateKey, list);
  }

  const sortByStart = (a: T, b: T) =>
    (a.startsAtIso ?? "").localeCompare(b.startsAtIso ?? "");

  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, dayEvents]) => ({
      dateKey,
      label: formatDaySectionLabel(dateKey, todayKey),
      events: [...dayEvents].sort(sortByStart),
    }));
}
